
import {SINGLETON_CLASS} from "@kta20-core/constants";
import {AlertOptions, SpinnerTypes} from "@ionic/core";
import {ErrorColorMap, IToastOptions, IUiServices} from "@kta20-core/interfaces/i-ui-services";
import {Translation} from "@kta20-ui-base/resources/lang/en";
import {ErrorSeverities} from "@kta20-core/errors/custom-error";
import errorToastTemplate from '@kta20-ui-base/view/error-toast-template';
import {KT_EE_LOGO_BASE64} from "@kta20-ui-base/comps/resources/icons";

const TOAST_CTRL = 'ion-toast-controller';
const MODAL_CTRL = 'ion-modal-controller';
const AS_ELEMENT = 'ion-action-sheet'

const TOAST_DISMISS_EVENT = 'ionToastDidDismiss';

const MODAL_DID_PRESENT = 'ionModalDidPresent';
const MODAL_WILL_DISMISS = 'ionModalWillDismiss';
const MODAL_DID_DISMISS = 'ionModalDidDismiss';

const AS_DID_PRESENT = 'ionActionSheetDidPresent';
const AS_WILL_PRESENT = 'ionActionSheetWillPresent';
const AS_DID_DISMISS = 'ionActionSheetDidDismiss';
const AS_WILL_DISMISS = 'ionActionSheetWillDismiss';

const ERROR_TOAST_ID = 'kta20-error-toast';

export class UiService implements IUiServices{

    private static inst:UiService;
    private tr:Translation;
    private modalCtrl:HTMLIonModalControllerElement;
    private modal:HTMLIonModalElement;
    private loading:HTMLIonLoadingElement;
    private errorToast:HTMLDivElement;
    private alert:HTMLIonAlertElement;
    private actionSheet:HTMLIonActionSheetElement;
    private onModalDidPresent:(data?: any) => void;
    private onModalWillDismiss:(data?: any) => void;
    private onModalDidDismiss:(data?: any) => void;
    private onActionSheetWillPresent:(data?: any) => void;
    private onActionSheetDidPresent:(data?: any) => void;
    private onActionSheetWillDismiss:(data?: any) => void;
    private onActionSheetDidDismiss:(data?: any) => void;


    constructor() {
        if (UiService.inst) throw new Error(SINGLETON_CLASS);
    }

    static get(T?:Translation): UiService {
        if (!this.inst) {
            this.inst = new UiService();
            this.inst.tr = T;
            this.inst.loading = null;
        }
        return this.inst;
    }

    isLoading(): boolean {
        return this.loading!=null;
    }

    async displayModal(
        contentComponentName:string,
        /**
         * Data must map content component public properties
         */
        data?:any,
        onDidPresent?:(data?: any) => void,
        onWillDismiss?:(data?: any) => void,
        onDidDismiss?:(data?: any) => void,
    ): Promise<void> {

        //if the not performed explicitly, do it...
        if(this.modal) await this.dismissModal();

        this.onModalDidDismiss = onDidDismiss ? onDidDismiss : (data?: any) => {data;};
        this.onModalDidPresent = onDidPresent ? onDidPresent : (data?: any) => {data;};
        this.onModalWillDismiss = onWillDismiss ? onWillDismiss : (data?: any) => {data;};

        const modalControllerElement = document.createElement(MODAL_CTRL);
        this.modalCtrl = <HTMLIonModalControllerElement>document.body.appendChild(modalControllerElement);

        this.modal = await this.modalCtrl.create({
            component: contentComponentName, //'modal-content'
            componentProps: data //data must map component props
        });

        this.modal.addEventListener(MODAL_DID_PRESENT,this.onModalDidPresent.bind(this));
        this.modal.addEventListener(MODAL_WILL_DISMISS,this.onModalWillDismiss.bind(this));
        this.modal.addEventListener(MODAL_DID_DISMISS,this.onModalDidDismiss.bind(this));

        await this.modal.present();
    }

    async dismissModal(data?:any): Promise<void> {

        await this.modal.dismiss(data);

        this.modal.removeEventListener(MODAL_DID_PRESENT,this.onModalDidPresent.bind(this));
        this.modal.removeEventListener(MODAL_WILL_DISMISS,this.onModalWillDismiss.bind(this));
        this.modal.removeEventListener(MODAL_DID_DISMISS,this.onModalDidDismiss.bind(this));

        const list = document.getElementsByName(MODAL_CTRL);
        if(list && list.length>0) list[0].remove();

        this.modalCtrl = null;
        this.modal = null;
    }

    private async getToastCtrl():Promise<HTMLIonToastControllerElement>{
        const results = document.getElementsByName(TOAST_CTRL);
        let toastController = <HTMLIonToastControllerElement>(results.length > 0 ? results[0] : document.createElement(TOAST_CTRL));
        document.body.appendChild(toastController);

        await toastController.componentOnReady();

        return toastController;
    }

    async displayToast(options: IToastOptions) {
        const toast = await (await this.getToastCtrl()).create(<any>options);
        await toast.present();
    }

    displayToastError(message: string, duration:number=0, severity: ErrorSeverities = ErrorSeverities.UNRECOVERABLE) {

        function dismiss() {
            this.errorToast.dispatchEvent(new CustomEvent(TOAST_DISMISS_EVENT));
            document.getElementById(ERROR_TOAST_ID).remove();
            this.errorToast = null;
        }

        //Only display one error message to the user.
        //if you don't check this, error toasts will
        //display one on top of each other.
        //By the way errors are logged all the times.
        if(!this.errorToast){
             const wrapper = document.createElement('div');

             wrapper.id=ERROR_TOAST_ID;
             wrapper.innerHTML = errorToastTemplate(message,ErrorColorMap[severity]);
             this.errorToast = document.body.appendChild(wrapper);

            const closeButton = this.errorToast.querySelector('svg');
             closeButton.onclick=dismiss.bind(this);

            if(duration>0) {
                closeButton.style.display='none';
                setTimeout(dismiss.bind(this),duration);
            }
        }
    }

    async displayLoading(
        message:string,
        showBackdrop:boolean=true,
        spinner:SpinnerTypes='lines',
        translucent:boolean=false,
        animated:boolean=true,
        backdropDismiss:boolean=false,
        cssClass:string='',
        duration:number=0,
        enterAnimation:any=null,
        keyboardClose:boolean=true,
        leaveAnimation:any=null
    ): Promise<void> {

        this.loading = <HTMLIonLoadingElement>document.createElement('ion-loading');

        //@ts-ignore
        if(spinner==='kt') this.loading.setAttribute('data-kta2-custom-loading', '');

        //@ts-ignore
        this.loading.message = (spinner!=='kt') ? message : `<img src="data:image/svg+xml;base64,${KT_EE_LOGO_BASE64}" /> <span>${message}</span>` ;
        //@ts-ignore
        this.loading.spinner = (spinner!=='kt') ? spinner : null;
        this.loading.showBackdrop = showBackdrop;
        this.loading.translucent = translucent;
        this.loading.animated = animated;
        this.loading.backdropDismiss = backdropDismiss;
        this.loading.cssClass = cssClass;
        this.loading.duration = duration;
        this.loading.enterAnimation = enterAnimation;
        this.loading.keyboardClose = keyboardClose;
        this.loading.leaveAnimation = leaveAnimation;

        document.body.appendChild(this.loading);
        return this.loading.present();
    }

    async dismissLoading():Promise<void>{
        if(this.loading) {
            await this.loading.dismiss();
            this.loading = null;
        }
    }

    async displaySessionExpireToast(): Promise<any> {
        return await this.displayToast(
        {
            message: this.tr.session_about_to_expire,
            duration: 20*1000,
            showCloseButton:true,
            closeButtonText:this.tr.close,
            position:'bottom',
            color:'warning'
            //keep for later in case we want to re-store this feature
            // buttons:[
            //     {
            //         side: 'end',
            //         icon: 'refresh',
            //         text: 'Extend...',
            //         handler: async () => {
            //             await authService.displayLogin(
            //                 config.authProvidersConfig[0].getLoginUrl(),
            //                 config.authProvidersConfig[0].logout_uri
            //             );
            //         }
            //     }
            // ]
        });
    }

    async displayAlert(alertOptions:AlertOptions):Promise<void> {

        //check if any already
        const list = document.getElementsByName('ion-alert');
        if(list && list.length>0) list[0].remove();

        this.alert = <HTMLIonAlertElement >document.createElement('ion-alert');

        Object.keys(alertOptions).forEach(key=>{
            this.alert[key]=alertOptions[key];
        });

        document.body.appendChild(this.alert);

        return this.alert.present();
    }

    async displayActionSheet(
        opts: any,
        onActionSheetDidDismiss?: (data?: any) => void,
        onActionSheetDidPresent?: (data?: any) => void,
        onActionSheetWillDismiss?: (data?: any) => void,
        onActionSheetWillPresent?: (data?: any) => void
    ) {
        if(this.actionSheet) await this.dismissActionSheet();

        this.onActionSheetDidDismiss = onActionSheetDidDismiss ? onActionSheetDidDismiss : (data?:any)=>{data;};
        this.onActionSheetDidPresent = onActionSheetDidPresent ? onActionSheetDidPresent : (data?:any)=>{data;};
        this.onActionSheetWillDismiss = onActionSheetWillDismiss ? onActionSheetWillDismiss : (data?:any)=>{data;};
        this.onActionSheetWillPresent = onActionSheetWillPresent ? onActionSheetWillPresent : (data?:any)=>{data;};

        this.actionSheet = <HTMLIonActionSheetElement>document.createElement(AS_ELEMENT);
        document.body.appendChild(this.actionSheet);

        this.actionSheet.addEventListener(AS_DID_DISMISS,this.onActionSheetDidDismiss);
        this.actionSheet.addEventListener(AS_DID_PRESENT,this.onActionSheetDidPresent);
        this.actionSheet.addEventListener(AS_WILL_DISMISS,this.onActionSheetWillDismiss);
        this.actionSheet.addEventListener(AS_WILL_PRESENT,this.onActionSheetWillPresent);

        opts = opts || {};

        Object.keys(opts).forEach(key=>this.actionSheet[key]=opts[key]);

        await this.actionSheet.present();
    }

    async dismissActionSheet(data?: any) {
        await this.actionSheet.dismiss(data);

        this.actionSheet.removeEventListener(AS_WILL_PRESENT,this.onActionSheetWillPresent);
        this.actionSheet.removeEventListener(AS_DID_PRESENT,this.onActionSheetDidPresent);
        this.actionSheet.removeEventListener(AS_DID_DISMISS,this.onActionSheetDidDismiss);
        this.actionSheet.removeEventListener(AS_WILL_DISMISS,this.onActionSheetWillDismiss);

        const list = document.getElementsByName(AS_ELEMENT);
        if(list && list.length>0) list[0].remove();

        this.actionSheet = null;
    }
}
